import { useCallback, useMemo, useState } from 'react';
import useDatasetStore from '../../../../store/datasetStore';
import { DownloadInfoItem, IBestDataset } from '../../../../types/analytics';
import { MarkerItem } from '../../../../types/home';
import { DatasetType } from '../../../../types/marker';
import { IEnrichedMetadata } from '../../../../types/metadata';
import { datasetType, ITEM, TabKey } from '../../../../utils/common';
import { isOfflineDatasetSelectionGuard } from '../../../../utils/offlineDatasetGuard';

interface AnalyticsCommonInputProps {
  downloadItems: DownloadInfoItem[];
  category: string | string[];
  coordinates: number[];
  previewDatasetId: string;
  point?: any;
  enrichedMetadata: IEnrichedMetadata[];
  showMesh: boolean;
  onMeshControl: (value: string) => void;
  updateMarker: (id: string, data: any) => void;
  markers: MarkerItem[];
  onChangeActiveKey: (item: any, key: TabKey) => void;
  setActiveDatasetKey: (
    markerId: string,
    datasetKey: DatasetType,
    newActiveDataset: string
  ) => void;
  setDatasetIsOfflineWarningVisible: (visible: boolean) => void;
}

export const useAnalyticsCommonInput = ({
  downloadItems,
  category,
  coordinates,
  point,
  showMesh,
  enrichedMetadata,
  previewDatasetId,
  markers,
  onMeshControl,
  setActiveDatasetKey,
  updateMarker,
  onChangeActiveKey,
  setDatasetIsOfflineWarningVisible,
}: AnalyticsCommonInputProps) => {
  const [temporaryDatasetName, setTemporaryDatasetName] = useState('');
  const [isChangeDatasetWarningVisible, setIsChangeDatasetWarningVisible] =
    useState(false);

  const datasetBriefs = useDatasetStore((s) => s.datasetBriefs);
  const categoryDataset = useMemo(() => {
    const selectedDataset: IBestDataset[] = [];

    downloadItems.forEach((element) => {
      const options = element.options.map((item) => {
        return {
          id: item.id,
          label: datasetType[element.radioKey],
          value: item.value,
        };
      });
      selectedDataset.push(...options);
    });

    return selectedDataset.length > 0 ? selectedDataset : [];
  }, [downloadItems]);

  const onDatasetValueChange = useCallback(
    (item: DownloadInfoItem) => {
      if (!point?.downloadPage) return;

      const idx = point.downloadPage.findIndex(
        (d: any) => d.radioKey === item.radioKey
      );

      if (idx < 0) return;

      const updatedDownloadPage = [...point.downloadPage];
      updatedDownloadPage[idx] = item;
      updateMarker(point.id, { downloadPage: updatedDownloadPage });
    },
    [point, updateMarker]
  );

  const changeDataset = useCallback(
    (newDataset: string) => {
      if (!point?.downloadPage) return;

      const datasetKey = Object.entries(point?.allDataset || {})
        .filter(([key]) => !key.includes('_Default'))
        .find(([key, datasets]) => {
          if (Array.isArray(datasets)) {
            return datasets.includes(newDataset);
          }
          return datasets === newDataset;
        })?.[0];

      const obj = Object.assign(
        {},
        point?.downloadPage.find((item: { radioKey: string }) => {
          return item.radioKey === datasetKey;
        }),
        { datasetId: newDataset }
      );

      onDatasetValueChange(obj);
      setActiveDatasetKey(point!.id, datasetKey as DatasetType, newDataset);
    },
    [
      point,
      // category,
      // showMesh,
      // onMeshControl,
      // updateMarker,
      onDatasetValueChange,
      // navigate,
      setActiveDatasetKey,
    ]
  );

  const onDatasetChange = useCallback(
    (value: string) => {
      if (
        isOfflineDatasetSelectionGuard({
          datasetId: value,
          datasetBriefs,
          setDatasetIsOfflineWarningVisible,
        })
      ) {
        return;
      }

      const datasetTask = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === previewDatasetId &&
          task.CoordinatesString === coordinates.join()
      );

      if (datasetTask === undefined) {
        changeDataset(value);
      } else {
        setTemporaryDatasetName(value);
        setIsChangeDatasetWarningVisible(true);
      }
    },
    [
      enrichedMetadata,
      changeDataset,
      coordinates,
      previewDatasetId,
      datasetBriefs,
      setDatasetIsOfflineWarningVisible,
    ]
  );

  const handleChangeDatasetWarningOk = useCallback(() => {
    setIsChangeDatasetWarningVisible(false);
    changeDataset(temporaryDatasetName ?? '');
    setTemporaryDatasetName('');
  }, [
    temporaryDatasetName,
    setIsChangeDatasetWarningVisible,
    changeDataset,
    setTemporaryDatasetName,
  ]);

  const handleChangeDatasetWarningCancel = useCallback(() => {
    setIsChangeDatasetWarningVisible(false);
    setTemporaryDatasetName('');
  }, [setIsChangeDatasetWarningVisible, setTemporaryDatasetName]);

  const hasExistingDatasetTask = useMemo(() => {
    return enrichedMetadata.some(
      (task: IEnrichedMetadata) =>
        task.DatasetId === previewDatasetId &&
        task.CoordinatesString === coordinates.join()
    );
  }, [enrichedMetadata, previewDatasetId, coordinates]);

  const pointOptions = useMemo(() => {
    const points = markers?.map((item) => ({
      label: item.label,
      value: item.id,
    }));
    return points ?? [];
  }, [markers]);

  const handleChangePoint = (item: any) => {
    onChangeActiveKey(item, ITEM.ANALYTICS);
  };

  return {
    categoryDataset,
    pointOptions,
    handleChangePoint,
    hasExistingDatasetTask,
    onDatasetChange,
    onDatasetValueChange,
    changeDataset,
    handleChangeDatasetWarningOk,
    handleChangeDatasetWarningCancel,
    isChangeDatasetWarningVisible,
  };
};
